import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { showToast } from '../Toast';
import { DSSheet, DSButton, DSInput, DSSelect, DSIcon } from '../../design-system/primitives';
import { recentMonths, monthLabel } from '../../pages/membership/months';
import { isCurrentMember } from '../../utils/membership';
import '../../pages/membership/membership.css';
import UserAvatar from '../UserAvatar';

// Recording cash, in the three shapes the workflow actually takes.
//
// The important thing this gets right, and an earlier version did not: the input
// is the MONTH the payment covers, not a number of months. Cash is the only
// method with no external evidence at all, so the paid-through date is the
// evidence, and an admin recording November's takings for October's dues has to
// be able to say which month they mean.
//
// Three entry points, one sheet:
//
//   - From Payments, with nobody chosen yet. Starts with a member picker.
//   - From a cash-due row in Needs attention, for one named member.
//   - The monthly round, several members ticked at once. This is the one that
//     matters most: collecting cash is a single ritual done at the club, and
//     six separate deep navigations is how it stops getting done.
//
// The amount is recorded but decides nothing. Somebody who hands over $40 has
// paid for that month; the difference is a conversation, not a state.
//
// "Paid with club credits" covers dues out of somebody's credit balance instead
// of cash in hand. Usually the member's own, sometimes a friend or partner
// covering them, so the payer is chosen separately and defaults to the member.
// The server refuses if the payer does not have the credits; it never runs
// anybody onto their tab for dues.

const DEFAULT_AMOUNT = '50.00';

// Square already collects from these, so recording dues by hand pays twice.
const SQUARE_METHODS = {
  SUBSCRIPTION: 'a Square subscription',
  INVOICE: 'Square invoices'
};

function RecordCashSheet({ member, defaultPaidWith = 'cash', onClose, onDone }) {
  const needsPicker = !member;

  // Next month first, so dues can be paid ahead, then this month and back.
  // Months already paid stay listed but disabled: the server refuses a second
  // payment for the same month, and it is better not to offer one.
  const paidMonths = useMemo(() => new Set((member && member.paidMonths) || []), [member]);
  const months = useMemo(() => {
    const now = new Date();
    return recentMonths(7, new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)));
  }, []);
  const [month, setMonth] = useState(() => {
    const thisMonth = months[1].value;
    if (!paidMonths.has(thisMonth)) return thisMonth;
    return paidMonths.has(months[0].value) ? thisMonth : months[0].value;
  });
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [picked, setPicked] = useState(member || null);
  const [candidates, setCandidates] = useState([]);

  const [paidWith, setPaidWith] = useState(defaultPaidWith);
  // null means "the member themselves", so it follows whoever is picked.
  const [payer, setPayer] = useState(null);
  const [payerBalance, setPayerBalance] = useState(null);
  const [picking, setPicking] = useState(false);

  const usingCredits = paidWith === 'credits';

  useEffect(() => {
    if (!needsPicker && !usingCredits) return undefined;
    if (candidates.length > 0) return undefined;
    let cancelled = false;
    apiService.getUsers()
      .then((response) => { if (!cancelled) setCandidates(response.data || []); })
      .catch(() => { if (!cancelled) setCandidates([]); });
    return () => { cancelled = true; };
  }, [needsPicker, usingCredits, candidates.length]);

  const memberId = picked ? (picked.userId || picked.id) : null;
  const payerId = usingCredits ? (payer ? payer.id : memberId) : null;

  useEffect(() => {
    setPayerBalance(null);
    if (!payerId) return undefined;
    let cancelled = false;
    apiService.getCreditBalance(payerId)
      .then((response) => { if (!cancelled) setPayerBalance(response.data.balance); })
      .catch(() => { if (!cancelled) setPayerBalance(null); });
    return () => { cancelled = true; };
  }, [payerId]);

  const amountValue = amount === '' ? null : Number(amount);
  const short = usingCredits && payerBalance != null && amountValue != null && payerBalance < amountValue;
  const blocked = (needsPicker && !picked)
    || paidMonths.has(month)
    || (usingCredits && (!payerId || !(amountValue > 0) || short));

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiService.recordCashPayment(memberId, {
        month,
        amount: amountValue == null ? undefined : amountValue,
        note: note || undefined,
        payFromUserId: payerId || undefined
      });
      showToast(
        usingCredits
          ? `Dues for ${monthLabel(month)} paid from ${payer ? `${displayName(payer)}'s` : 'their own'} credits`
          : `Cash payment recorded for ${monthLabel(month)}`,
        'success'
      );
      if (onDone) onDone();
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not record the payment', 'error');
      setSubmitting(false);
    }
  };

  let title = member ? `Record cash payment for ${member.name}` : 'Record a cash payment';
  if (usingCredits) title = member ? `Pay dues for ${member.name}` : 'Pay dues from credits';

  const body = member
    ? member.detail
    : 'For a member who pays at the club. Logged against the month, not the date received.';

  const squareMethod = member && SQUARE_METHODS[member.method];

  return (
    <DSSheet
      title={title}
      onClose={onClose}
      footer={(
        <>
          <DSButton appearance="outline" tone="neutral" onClick={onClose} disabled={submitting}>
            Cancel
          </DSButton>
          <DSButton appearance="solid" tone="primary" onClick={submit} disabled={submitting || blocked}>
            {submitting ? 'Working...' : 'Record payment'}
          </DSButton>
        </>
      )}
    >
      {body && <p className="cash-sheet__body">{body}</p>}

      {squareMethod && (
        <p className="cash-sheet__warning">
          <DSIcon name="warning" size="sm" />
          {member.name} pays by {squareMethod}. Recording dues here charges them twice for the month.
        </p>
      )}

      {needsPicker && (
        <div className="cash-sheet__field">
          <span className="cash-sheet__label">Member</span>

          {picked ? (
            <PickedAccount user={picked} onChange={() => setPicked(null)} />
          ) : (
            <AccountSearch id="cash-member" candidates={candidates} onPick={setPicked} autoFocus />
          )}

          {/* Recording money is not a membership decision, and the two get
              confused constantly. Said here so nobody records a payment
              expecting it to bring somebody back. */}
          {picked && !isCurrentMember(picked) && (
            <p className="cash-sheet__warning">
              <DSIcon name="warning" size="sm" />
              This person is not a current member. Recording a payment does not restore
              their membership on its own.
            </p>
          )}
        </div>
      )}

      <DSSelect
        id="cash-paid-with"
        label="Paid with"
        value={paidWith}
        onChange={(event) => setPaidWith(event.target.value)}
      >
        <option value="cash">Cash</option>
        <option value="credits">Club credits</option>
      </DSSelect>

      {usingCredits && (
        <div className="cash-sheet__field">
          <span className="cash-sheet__label">Take credits from</span>
          {payer || picked ? (
            <PickedAccount
              user={payer || picked}
              meta={payerBalance != null ? `Balance $${payerBalance.toFixed(2)}` : 'Loading balance...'}
              onChange={() => setPicking(true)}
              changeLabel={payer ? 'Change' : 'Someone else'}
            />
          ) : (
            <p className="cash-sheet__help">Choose the member first.</p>
          )}
          {picking && (
            <AccountSearch
              id="cash-payer"
              candidates={candidates}
              onPick={(user) => { setPayer(user); setPicking(false); }}
              autoFocus
            />
          )}
          {short && (
            <p className="cash-sheet__warning">
              <DSIcon name="warning" size="sm" />
              Not enough credits. Dues are never put on a tab.
            </p>
          )}
        </div>
      )}

      <DSSelect
        id="cash-month"
        label="Paid for month"
        value={month}
        onChange={(event) => setMonth(event.target.value)}
      >
        {months.map((option) => (
          <option key={option.value} value={option.value} disabled={paidMonths.has(option.value)}>
            {paidMonths.has(option.value) ? `${option.label} (already paid)` : option.label}
          </option>
        ))}
      </DSSelect>

      <DSInput
        id="cash-amount"
        label="Amount"
        type="number"
        min="0"
        step="0.01"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        helpText="A smaller amount still covers the month in full."
      />

      <DSInput
        id="cash-note"
        label="Note"
        placeholder="Optional"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
    </DSSheet>
  );
}

function PickedAccount({ user, meta, onChange, changeLabel = 'Change' }) {
  return (
    <div className="cash-sheet__picked">
      <UserAvatar person={user} className="cash-sheet__initials" />
      <span className="cash-sheet__picked-main">
        <span className="cash-sheet__picked-name">{displayName(user)}</span>
        <span className="cash-sheet__picked-meta">{meta || user.email}</span>
      </span>
      <DSButton appearance="outline" tone="neutral" size="sm" onClick={onChange}>
        {changeLabel}
      </DSButton>
    </div>
  );
}

function AccountSearch({ id, candidates, onPick, autoFocus }) {
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    return candidates.filter((user) => {
      const name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      return name.includes(term) || (user.email || '').toLowerCase().includes(term);
    }).slice(0, 8);
  }, [candidates, query]);

  return (
    <>
      <DSInput
        id={id}
        placeholder="Start typing a name"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoFocus={autoFocus}
      />
      {results.length > 0 && (
        <div className="cash-sheet__results">
          {results.map((user) => (
            <button
              key={user.id}
              type="button"
              className="cash-sheet__result"
              onClick={() => onPick(user)}
            >
              <UserAvatar person={user} className="cash-sheet__initials" />
              <span className="cash-sheet__picked-main">
                <span className="cash-sheet__picked-name">{displayName(user)}</span>
                <span className="cash-sheet__picked-meta">{user.email}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {query.trim().length >= 2 && results.length === 0 && (
        <p className="cash-sheet__help">
          No member matches that. Check the spelling, or they may not have an account yet.
        </p>
      )}
    </>
  );
}

// Accepts both a user row and the `{ userId, name }` shape callers pass as `member`.
function displayName(user) {
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.email;
}

export default RecordCashSheet;
